import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

const SAVED_SCREENS_KEY = 'saved_screens';
const LIVE_COUNT_CAP = 50;
const LIVE_COUNT_DEBOUNCE_MS = 700;

// ── Shared types (mirrors ScreenerFilters.tsx exactly) ──────────────────────

interface SavedScreen {
  id: string;
  name: string;
  criteria: ScreenerCriteria;
}

interface Region {
  code: string;
  name: string;
  exchange: string;
}

interface BroadRegion {
  code: string;
  name: string;
}

interface Preset {
  id: string;
  name: string;
  description: string;
  criteria: any;
}

interface MarketIndexInfo {
  code: string;
  name: string;
  country: string;
  constituent_count: number | null;
}

export interface ScreenerCriteria {
  regions: string[];
  index_codes?: string[];
  min_price?: number | null;
  max_price?: number | null;
  min_market_cap?: number | null;
  max_market_cap?: number | null;
  min_pe_ratio?: number | null;
  max_pe_ratio?: number | null;
  min_dividend_yield?: number | null;
  max_dividend_yield?: number | null;
  min_volume?: number | null;
  sectors?: string[];
  industries?: string[];
  max_results?: number;
  sma20_filter?: string | null;
  sma50_filter?: string | null;
  sma200_filter?: string | null;
  at_52w_high?: boolean;
  at_52w_low?: boolean;
  max_pct_from_52w_low?: number | null;
  max_pct_from_52w_high?: number | null;
  min_rsi?: number | null;
  max_rsi?: number | null;
  min_beta?: number | null;
  max_beta?: number | null;
  min_vol_ratio?: number | null;
  max_vol_ratio?: number | null;
  min_vol_change?: number | null;
  max_vol_change?: number | null;
  min_analyst_rating?: number | null;
  max_analyst_rating?: number | null;
  min_ev_ebitda?: number | null;
  max_ev_ebitda?: number | null;
  min_price_to_book?: number | null;
  max_price_to_book?: number | null;
  min_price_to_sales?: number | null;
  max_price_to_sales?: number | null;
  min_roe?: number | null;
  max_roe?: number | null;
  min_fcf_yield?: number | null;
  max_fcf_yield?: number | null;
  min_debt_to_equity?: number | null;
  max_debt_to_equity?: number | null;
  min_net_debt_to_ebitda?: number | null;
  max_net_debt_to_ebitda?: number | null;
}

interface ScreenerWizardProps {
  criteria: ScreenerCriteria;
  onFilterChange: (criteria: ScreenerCriteria) => void;
  onSearch: (criteria?: ScreenerCriteria) => void;
  onLoadSavedScreen?: (criteria: ScreenerCriteria) => void;
  onReset?: () => void;
  loading: boolean;
  hasSearched?: boolean;
  resultCount?: number;
}

const COMPACT_INPUT_SX = {
  '& .MuiInputBase-input': { fontSize: '0.85rem', py: 1.1, px: 1.25 },
  '& .MuiInputLabel-root': { fontSize: '0.8rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.25 },
};

const formatMarketCap = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value}`;
};

const rangeSummary = (label: string, min?: number | null, max?: number | null, suffix = ''): string | null => {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${label} ${min}${suffix}–${max}${suffix}`;
  if (min != null) return `${label} > ${min}${suffix}`;
  return `${label} < ${max}${suffix}`;
};

// ── Step-2 filter registry — every non-universe criterion, categorized ──────

type FilterKey =
  | 'price' | 'mcap' | 'pe' | 'ev_ebitda' | 'pb' | 'ps'
  | 'roe' | 'fcf' | 'de' | 'nde'
  | 'div' | 'beta' | 'analyst'
  | 'rsi' | 'sma' | 'week52' | 'volratio' | 'volchg';

interface FilterDef {
  key: FilterKey;
  label: string;
  category: 'Valuation' | 'Quality & Profitability' | 'Income & Risk' | 'Technical';
  isActive: (c: ScreenerCriteria) => boolean;
  clear: (c: ScreenerCriteria) => ScreenerCriteria;
  summary: (c: ScreenerCriteria) => string | null;
}

const FILTER_REGISTRY: FilterDef[] = [
  {
    key: 'price', label: 'Price', category: 'Valuation',
    isActive: c => c.min_price != null || c.max_price != null,
    clear: c => ({ ...c, min_price: null, max_price: null }),
    summary: c => rangeSummary('Price', c.min_price, c.max_price),
  },
  {
    key: 'mcap', label: 'Market Cap', category: 'Valuation',
    isActive: c => c.min_market_cap != null || c.max_market_cap != null,
    clear: c => ({ ...c, min_market_cap: null, max_market_cap: null }),
    summary: c => {
      if (c.min_market_cap == null && c.max_market_cap == null) return null;
      if (c.min_market_cap != null && c.max_market_cap != null) return `Mkt Cap ${formatMarketCap(c.min_market_cap)}–${formatMarketCap(c.max_market_cap)}`;
      if (c.min_market_cap != null) return `Mkt Cap > ${formatMarketCap(c.min_market_cap)}`;
      return `Mkt Cap < ${formatMarketCap(c.max_market_cap!)}`;
    },
  },
  {
    key: 'pe', label: 'P/E Ratio', category: 'Valuation',
    isActive: c => c.min_pe_ratio != null || c.max_pe_ratio != null,
    clear: c => ({ ...c, min_pe_ratio: null, max_pe_ratio: null }),
    summary: c => rangeSummary('P/E', c.min_pe_ratio, c.max_pe_ratio),
  },
  {
    key: 'ev_ebitda', label: 'EV / EBITDA', category: 'Valuation',
    isActive: c => c.min_ev_ebitda != null || c.max_ev_ebitda != null,
    clear: c => ({ ...c, min_ev_ebitda: null, max_ev_ebitda: null }),
    summary: c => rangeSummary('EV/EBITDA', c.min_ev_ebitda, c.max_ev_ebitda),
  },
  {
    key: 'pb', label: 'Price / Book', category: 'Valuation',
    isActive: c => c.min_price_to_book != null || c.max_price_to_book != null,
    clear: c => ({ ...c, min_price_to_book: null, max_price_to_book: null }),
    summary: c => rangeSummary('P/B', c.min_price_to_book, c.max_price_to_book),
  },
  {
    key: 'ps', label: 'Price / Sales', category: 'Valuation',
    isActive: c => c.min_price_to_sales != null || c.max_price_to_sales != null,
    clear: c => ({ ...c, min_price_to_sales: null, max_price_to_sales: null }),
    summary: c => rangeSummary('P/S', c.min_price_to_sales, c.max_price_to_sales),
  },
  {
    key: 'roe', label: 'ROE %', category: 'Quality & Profitability',
    isActive: c => c.min_roe != null || c.max_roe != null,
    clear: c => ({ ...c, min_roe: null, max_roe: null }),
    summary: c => rangeSummary('ROE', c.min_roe, c.max_roe, '%'),
  },
  {
    key: 'fcf', label: 'FCF Yield %', category: 'Quality & Profitability',
    isActive: c => c.min_fcf_yield != null || c.max_fcf_yield != null,
    clear: c => ({ ...c, min_fcf_yield: null, max_fcf_yield: null }),
    summary: c => rangeSummary('FCF Yield', c.min_fcf_yield, c.max_fcf_yield, '%'),
  },
  {
    key: 'de', label: 'Debt / Equity', category: 'Quality & Profitability',
    isActive: c => c.min_debt_to_equity != null || c.max_debt_to_equity != null,
    clear: c => ({ ...c, min_debt_to_equity: null, max_debt_to_equity: null }),
    summary: c => rangeSummary('D/E', c.min_debt_to_equity, c.max_debt_to_equity),
  },
  {
    key: 'nde', label: 'Net Debt / EBITDA', category: 'Quality & Profitability',
    isActive: c => c.min_net_debt_to_ebitda != null || c.max_net_debt_to_ebitda != null,
    clear: c => ({ ...c, min_net_debt_to_ebitda: null, max_net_debt_to_ebitda: null }),
    summary: c => rangeSummary('Net Debt/EBITDA', c.min_net_debt_to_ebitda, c.max_net_debt_to_ebitda),
  },
  {
    key: 'div', label: 'Dividend Yield', category: 'Income & Risk',
    isActive: c => c.min_dividend_yield != null || c.max_dividend_yield != null,
    clear: c => ({ ...c, min_dividend_yield: null, max_dividend_yield: null }),
    summary: c => rangeSummary('Div Yield', c.min_dividend_yield, c.max_dividend_yield, '%'),
  },
  {
    key: 'beta', label: 'Beta', category: 'Income & Risk',
    isActive: c => c.min_beta != null || c.max_beta != null,
    clear: c => ({ ...c, min_beta: null, max_beta: null }),
    summary: c => rangeSummary('Beta', c.min_beta, c.max_beta),
  },
  {
    key: 'analyst', label: 'Analyst Rating', category: 'Income & Risk',
    isActive: c => c.min_analyst_rating != null || c.max_analyst_rating != null,
    clear: c => ({ ...c, min_analyst_rating: null, max_analyst_rating: null }),
    summary: c => rangeSummary('Analyst Rating', c.min_analyst_rating, c.max_analyst_rating),
  },
  {
    key: 'rsi', label: 'RSI', category: 'Technical',
    isActive: c => c.min_rsi != null || c.max_rsi != null,
    clear: c => ({ ...c, min_rsi: null, max_rsi: null }),
    summary: c => rangeSummary('RSI', c.min_rsi, c.max_rsi),
  },
  {
    key: 'sma', label: 'Moving Averages', category: 'Technical',
    isActive: c => !!(c.sma20_filter || c.sma50_filter || c.sma200_filter),
    clear: c => ({ ...c, sma20_filter: null, sma50_filter: null, sma200_filter: null }),
    summary: c => {
      const parts: string[] = [];
      if (c.sma20_filter) parts.push(`20MA ${c.sma20_filter}`);
      if (c.sma50_filter) parts.push(`50MA ${c.sma50_filter}`);
      if (c.sma200_filter) parts.push(`200MA ${c.sma200_filter}`);
      return parts.length ? parts.join(', ') : null;
    },
  },
  {
    key: 'week52', label: '52-Week Position', category: 'Technical',
    isActive: c => c.max_pct_from_52w_high != null || c.max_pct_from_52w_low != null || !!c.at_52w_high || !!c.at_52w_low,
    clear: c => ({ ...c, max_pct_from_52w_high: null, max_pct_from_52w_low: null, at_52w_high: undefined, at_52w_low: undefined }),
    summary: c => {
      const parts: string[] = [];
      if (c.max_pct_from_52w_high != null) parts.push(`within ${c.max_pct_from_52w_high}% of 52W Hi`);
      if (c.max_pct_from_52w_low != null) parts.push(`within ${c.max_pct_from_52w_low}% of 52W Lo`);
      if (c.at_52w_high) parts.push('near 52W High');
      if (c.at_52w_low) parts.push('near 52W Low');
      return parts.length ? parts.join(', ') : null;
    },
  },
  {
    key: 'volratio', label: 'Vol vs Avg', category: 'Technical',
    isActive: c => c.min_vol_ratio != null || c.max_vol_ratio != null,
    clear: c => ({ ...c, min_vol_ratio: null, max_vol_ratio: null }),
    summary: c => rangeSummary('Vol vs Avg', c.min_vol_ratio, c.max_vol_ratio, 'x'),
  },
  {
    key: 'volchg', label: 'Vol Day Chg%', category: 'Technical',
    isActive: c => c.min_vol_change != null || c.max_vol_change != null,
    clear: c => ({ ...c, min_vol_change: null, max_vol_change: null }),
    summary: c => rangeSummary('Vol Day Chg', c.min_vol_change, c.max_vol_change, '%'),
  },
];

const CATEGORIES: FilterDef['category'][] = ['Valuation', 'Quality & Profitability', 'Income & Risk', 'Technical'];

const PRESET_CHIP_SX = { height: 26, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' as const };

const ScreenerWizard: React.FC<ScreenerWizardProps> = ({
  criteria,
  onFilterChange,
  onSearch,
  onLoadSavedScreen,
  onReset,
  loading,
  hasSearched,
  resultCount,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [manuallyAdded, setManuallyAdded] = useState<Set<FilterKey>>(new Set());
  const [search, setSearch] = useState('');
  // Once a screen has actually run, fold the wizard down to a compact summary
  // bar so results get the room — "Edit Screen" re-expands it without losing
  // any state. Runs from step 3 also fold it back down automatically.
  const [forceOpen, setForceOpen] = useState(false);

  const [broadRegions, setBroadRegions] = useState<BroadRegion[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [availableIndices, setAvailableIndices] = useState<MarketIndexInfo[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [savedScreens, setSavedScreens] = useState<SavedScreen[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveScreenName, setSaveScreenName] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [liveCountLoading, setLiveCountLoading] = useState(false);

  // ── Static data (identical to ScreenerFilters.tsx) ────────────────────────
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/screener/regions`)
      .then(res => res.json())
      .then(data => { setBroadRegions(data.broad_regions || []); setRegions(data.regions || []); })
      .catch(err => console.error('Error fetching regions:', err));

    fetch(`${API_BASE_URL}/api/indices`)
      .then(res => res.json())
      .then(data => setAvailableIndices(data.indices || []))
      .catch(err => console.error('Error fetching indices:', err));

    fetch(`${API_BASE_URL}/api/screener/sectors`)
      .then(res => res.json())
      .then(data => setSectors(data.sectors || []))
      .catch(err => console.error('Error fetching sectors:', err));

    fetch(`${API_BASE_URL}/api/screener/presets`)
      .then(res => res.json())
      .then(data => setPresets(data.presets || []))
      .catch(err => console.error('Error fetching presets:', err));
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SAVED_SCREENS_KEY);
      if (stored) setSavedScreens(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (criteria.regions.length > 0) params.set('regions', criteria.regions.join(','));
    if (criteria.sectors && criteria.sectors.length > 0) params.set('sectors', criteria.sectors.join(','));
    const qs = params.toString();
    fetch(`${API_BASE_URL}/api/screener/industries${qs ? `?${qs}` : ''}`)
      .then(res => res.json())
      .then(data => setIndustries(data.industries || []))
      .catch(err => console.error('Error fetching industries:', err));
  }, [criteria.regions, criteria.sectors]);

  // ── Keep manuallyAdded in sync: anything active in criteria stays visible;
  // nothing is ever auto-removed (only the explicit ✕ button removes a group) ──
  useEffect(() => {
    setManuallyAdded(prev => {
      let changed = false;
      const next = new Set(prev);
      FILTER_REGISTRY.forEach(f => {
        if (f.isActive(criteria) && !next.has(f.key)) { next.add(f.key); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [criteria]);

  // ── Detect an external reset (StockScreener's handleReset) and snap the
  // wizard back to step 1 with nothing added ──────────────────────────────
  const wasPristine = useRef(true);
  useEffect(() => {
    const isPristine = criteria.regions.length === 0
      && (!criteria.index_codes || criteria.index_codes.length === 0)
      && FILTER_REGISTRY.every(f => !f.isActive(criteria));
    if (isPristine && !wasPristine.current) {
      setStep(1);
      setManuallyAdded(new Set());
    }
    wasPristine.current = isPristine;
  }, [criteria]);

  // ── Live match-count estimate (debounced) — reuses the real screen
  // endpoint capped at LIVE_COUNT_CAP results, with count_only:true so the
  // backend skips its unconditional technical-indicator/performance passes
  // (otherwise ~10s per call regardless of filters used — those exist only
  // to decorate the results table's Technical/Performance tabs, which a
  // live estimate never renders). Also aborts a still-in-flight probe when
  // the next edit lands, so rapid filter changes can't stack up concurrent
  // heavy requests against the backend. ──────────────────────────────────
  const hasUniverse = criteria.regions.length > 0 || (criteria.index_codes && criteria.index_codes.length > 0);
  useEffect(() => {
    if (!hasUniverse) { setLiveCount(null); setLiveCountLoading(false); return; }
    setLiveCountLoading(true);
    const controller = new AbortController();
    const t = setTimeout(() => {
      fetch(`${API_BASE_URL}/api/screener/screen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...criteria, max_results: LIVE_COUNT_CAP, count_only: true }),
        signal: controller.signal,
      })
        .then(r => (r.ok ? r.json() : Promise.reject(r)))
        .then(d => setLiveCount((d.results || []).length))
        .catch((err) => { if (err?.name !== 'AbortError') setLiveCount(null); })
        .finally(() => setLiveCountLoading(false));
    }, LIVE_COUNT_DEBOUNCE_MS);
    return () => { clearTimeout(t); controller.abort(); };
    // eslint-disable-next-line
  }, [JSON.stringify(criteria)]);

  const handleSaveScreen = () => {
    if (!saveScreenName.trim()) return;
    const screen: SavedScreen = { id: `ss_${Date.now().toString(36)}`, name: saveScreenName.trim(), criteria: { ...criteria } };
    const updated = [...savedScreens, screen];
    setSavedScreens(updated);
    localStorage.setItem(SAVED_SCREENS_KEY, JSON.stringify(updated));
    setSaveDialogOpen(false);
    setSaveScreenName('');
    setSnackbar({ open: true, message: `Screen "${screen.name}" saved`, severity: 'success' });
  };

  const criteriaHasUniverse = (c: ScreenerCriteria) => c.regions.length > 0 || !!(c.index_codes && c.index_codes.length > 0);

  // Presets (screener.py's predefined screens) only carry technical/fundamental
  // criteria — no region. Applying one on its own used to fire a search with
  // zero regions and hit the backend's "at least one region" error. Default
  // to US when nothing else is set, and land on Step 1 (Universe) instead of
  // auto-running, so the assumed default is visible and changeable before
  // anything runs — never a silent assumption.
  const applyExternalCriteria = (c: ScreenerCriteria): ScreenerCriteria => {
    const withUniverse = criteriaHasUniverse(c) ? c : { ...c, regions: ['US'] };
    const added = new Set<FilterKey>();
    FILTER_REGISTRY.forEach(f => { if (f.isActive(withUniverse)) added.add(f.key); });
    setManuallyAdded(added);
    setStep(criteriaHasUniverse(c) ? 3 : 1);
    return withUniverse;
  };

  const handleLoadSavedScreen = (screen: SavedScreen) => {
    const hadUniverse = criteriaHasUniverse(screen.criteria);
    const withUniverse = applyExternalCriteria(screen.criteria);
    onFilterChange(withUniverse);
    if (!hadUniverse) return; // wait for explicit confirmation on Review
    if (onLoadSavedScreen) onLoadSavedScreen(withUniverse);
    else onSearch(withUniverse);
  };

  const handleDeleteSavedScreen = (id: string) => {
    const updated = savedScreens.filter(s => s.id !== id);
    setSavedScreens(updated);
    localStorage.setItem(SAVED_SCREENS_KEY, JSON.stringify(updated));
  };

  const handlePresetClick = (preset: Preset) => {
    const merged = { ...criteria, ...preset.criteria };
    const hadUniverse = criteriaHasUniverse(merged);
    const withUniverse = applyExternalCriteria(merged);
    onFilterChange(withUniverse);
    if (hadUniverse) onSearch(withUniverse); // already had a universe — keep the one-click convenience
  };

  const handleRegionToggle = (regionCode: string) => {
    const newRegions = criteria.regions.includes(regionCode)
      ? criteria.regions.filter(r => r !== regionCode)
      : [...criteria.regions, regionCode];
    onFilterChange({ ...criteria, regions: newRegions });
  };

  const handleIndexToggle = (indexCode: string) => {
    const current = criteria.index_codes || [];
    const newIndices = current.includes(indexCode) ? current.filter(c => c !== indexCode) : [...current, indexCode];
    onFilterChange({ ...criteria, index_codes: newIndices });
  };

  const handleSectorChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFilterChange({ ...criteria, sectors: typeof value === 'string' ? value.split(',') : value });
  };

  const handleIndustryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFilterChange({ ...criteria, industries: typeof value === 'string' ? value.split(',') : value });
  };

  const handleNumberChange = (field: keyof ScreenerCriteria, value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    onFilterChange({ ...criteria, [field]: numValue } as ScreenerCriteria);
  };

  const toggleSmaFilter = (period: 'sma20_filter' | 'sma50_filter' | 'sma200_filter', direction: 'above' | 'below') => {
    const current = criteria[period];
    onFilterChange({ ...criteria, [period]: current === direction ? null : direction });
  };

  const addFilter = (key: FilterKey) => setManuallyAdded(prev => new Set(prev).add(key));

  const removeFilter = (def: FilterDef) => {
    onFilterChange(def.clear(criteria));
    setManuallyAdded(prev => { const next = new Set(prev); next.delete(def.key); return next; });
  };

  const visibleGroups = useMemo(
    () => FILTER_REGISTRY.filter(f => manuallyAdded.has(f.key) || f.isActive(criteria)),
    [manuallyAdded, criteria]
  );

  const activeSummaries = useMemo(() => {
    const items: string[] = [];
    if (criteria.regions.length > 0) items.push(criteria.regions.length <= 3 ? `Universe: ${criteria.regions.join(', ')}` : `Universe: ${criteria.regions.length} regions`);
    if (criteria.index_codes && criteria.index_codes.length > 0) items.push(`${criteria.index_codes.length} index(es)`);
    if (criteria.sectors && criteria.sectors.length > 0) items.push(`${criteria.sectors.length} sector(s)`);
    if (criteria.industries && criteria.industries.length > 0) items.push(`${criteria.industries.length} industry(ies)`);
    FILTER_REGISTRY.forEach(f => { const s = f.summary(criteria); if (s) items.push(s); });
    return items;
  }, [criteria]);

  const searchLower = search.trim().toLowerCase();

  // ── Per-filter input bodies — ported 1:1 from ScreenerFilters.tsx ─────────
  const renderFilterInputs = (key: FilterKey) => {
    switch (key) {
      case 'price':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_price || ''}
              onChange={(e) => handleNumberChange('min_price', e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }} sx={COMPACT_INPUT_SX} />
            <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_price || ''}
              onChange={(e) => handleNumberChange('max_price', e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.01 } }} sx={COMPACT_INPUT_SX} />
          </Box>
        );
      case 'mcap':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Small" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_market_cap: 300_000_000, max_market_cap: 2_000_000_000 })} />
              <Chip label="Mid" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_market_cap: 2_000_000_000, max_market_cap: 10_000_000_000 })} />
              <Chip label="Large" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_market_cap: 10_000_000_000, max_market_cap: null })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_market_cap || ''}
                onChange={(e) => handleNumberChange('min_market_cap', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 1000000 } }}
                helperText={criteria.min_market_cap ? formatMarketCap(criteria.min_market_cap) : ''} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_market_cap || ''}
                onChange={(e) => handleNumberChange('max_market_cap', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 1000000 } }}
                helperText={criteria.max_market_cap ? formatMarketCap(criteria.max_market_cap) : ''} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'pe':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_pe_ratio || ''}
              onChange={(e) => handleNumberChange('min_pe_ratio', e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_pe_ratio || ''}
              onChange={(e) => handleNumberChange('max_pe_ratio', e.target.value)}
              InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
          </Box>
        );
      case 'ev_ebitda':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Cheap <10" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_ev_ebitda: null, max_ev_ebitda: 10 })} />
              <Chip label="Fair 10-20" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_ev_ebitda: 10, max_ev_ebitda: 20 })} />
              <Chip label="Rich >20" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_ev_ebitda: 20, max_ev_ebitda: null })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_ev_ebitda ?? ''}
                onChange={(e) => handleNumberChange('min_ev_ebitda', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_ev_ebitda ?? ''}
                onChange={(e) => handleNumberChange('max_ev_ebitda', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'pb':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_price_to_book ?? ''}
              onChange={(e) => handleNumberChange('min_price_to_book', e.target.value)}
              InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_price_to_book ?? ''}
              onChange={(e) => handleNumberChange('max_price_to_book', e.target.value)}
              InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
          </Box>
        );
      case 'ps':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_price_to_sales ?? ''}
              onChange={(e) => handleNumberChange('min_price_to_sales', e.target.value)}
              InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_price_to_sales ?? ''}
              onChange={(e) => handleNumberChange('max_price_to_sales', e.target.value)}
              InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
          </Box>
        );
      case 'roe':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="High >20%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_roe: 20, max_roe: null })} />
              <Chip label="Solid 10-20%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_roe: 10, max_roe: 20 })} />
              <Chip label="Negative" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_roe: null, max_roe: 0 })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min %" type="number" size="small" fullWidth value={criteria.min_roe ?? ''}
                onChange={(e) => handleNumberChange('min_roe', e.target.value)}
                InputProps={{ inputProps: { step: 1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max %" type="number" size="small" fullWidth value={criteria.max_roe ?? ''}
                onChange={(e) => handleNumberChange('max_roe', e.target.value)}
                InputProps={{ inputProps: { step: 1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'fcf':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Cash cow >5%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_fcf_yield: 5, max_fcf_yield: null })} />
              <Chip label="Decent 2-5%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_fcf_yield: 2, max_fcf_yield: 5 })} />
              <Chip label="Burning <0%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_fcf_yield: null, max_fcf_yield: 0 })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min %" type="number" size="small" fullWidth value={criteria.min_fcf_yield ?? ''}
                onChange={(e) => handleNumberChange('min_fcf_yield', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max %" type="number" size="small" fullWidth value={criteria.max_fcf_yield ?? ''}
                onChange={(e) => handleNumberChange('max_fcf_yield', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'de':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Low <0.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_debt_to_equity: null, max_debt_to_equity: 0.5 })} />
              <Chip label="Mid 0.5-1.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_debt_to_equity: 0.5, max_debt_to_equity: 1.5 })} />
              <Chip label="High >1.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_debt_to_equity: 1.5, max_debt_to_equity: null })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_debt_to_equity ?? ''}
                onChange={(e) => handleNumberChange('min_debt_to_equity', e.target.value)}
                InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_debt_to_equity ?? ''}
                onChange={(e) => handleNumberChange('max_debt_to_equity', e.target.value)}
                InputProps={{ inputProps: { step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'nde':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Net cash <0" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_net_debt_to_ebitda: null, max_net_debt_to_ebitda: 0 })} />
              <Chip label="Safe <2x" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_net_debt_to_ebitda: null, max_net_debt_to_ebitda: 2 })} />
              <Chip label="Stressed >4x" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_net_debt_to_ebitda: 4, max_net_debt_to_ebitda: null })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_net_debt_to_ebitda ?? ''}
                onChange={(e) => handleNumberChange('min_net_debt_to_ebitda', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_net_debt_to_ebitda ?? ''}
                onChange={(e) => handleNumberChange('max_net_debt_to_ebitda', e.target.value)}
                InputProps={{ inputProps: { step: 0.5 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'div':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Min %" type="number" size="small" fullWidth value={criteria.min_dividend_yield ?? ''}
              onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, min_dividend_yield: v }); }}
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            <TextField label="Max %" type="number" size="small" fullWidth value={criteria.max_dividend_yield ?? ''}
              onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, max_dividend_yield: v }); }}
              InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
          </Box>
        );
      case 'beta':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Low <1" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_beta: null, max_beta: 1.0 })} />
              <Chip label="Mid 1-1.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_beta: 1.0, max_beta: 1.5 })} />
              <Chip label="High >1.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_beta: 1.5, max_beta: null })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_beta ?? ''}
                onChange={(e) => handleNumberChange('min_beta', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_beta ?? ''}
                onChange={(e) => handleNumberChange('max_beta', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'analyst':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label="Strong Buy 4.5-5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_analyst_rating: 4.5, max_analyst_rating: 5 })} />
              <Chip label="Buy 3.5-4.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_analyst_rating: 3.5, max_analyst_rating: 4.5 })} />
              <Chip label="Hold 2.5-3.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_analyst_rating: 2.5, max_analyst_rating: 3.5 })} />
              <Chip label="Sell <2.5" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_analyst_rating: 1, max_analyst_rating: 2.5 })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_analyst_rating ?? ''}
                onChange={(e) => handleNumberChange('min_analyst_rating', e.target.value)}
                InputProps={{ inputProps: { min: 1, max: 5, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_analyst_rating ?? ''}
                onChange={(e) => handleNumberChange('max_analyst_rating', e.target.value)}
                InputProps={{ inputProps: { min: 1, max: 5, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'rsi':
        return (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField label="Min" type="number" size="small" fullWidth value={criteria.min_rsi ?? ''}
                onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, min_rsi: v }); }}
                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max" type="number" size="small" fullWidth value={criteria.max_rsi ?? ''}
                onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, max_rsi: v }); }}
                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip label=">70 Overbought" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_rsi: 70, max_rsi: null })} />
              <Chip label="<30 Oversold" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_rsi: null, max_rsi: 30 })} />
            </Box>
          </>
        );
      case 'sma':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {([
              ['sma20_filter', '20 MA'] as const,
              ['sma50_filter', '50 MA'] as const,
              ['sma200_filter', '200 MA'] as const,
            ]).map(([field, label]) => (
              <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', width: 46, flexShrink: 0, fontWeight: 500 }}>{label}</Typography>
                <Chip label="Above" size="small" variant={criteria[field] === 'above' ? 'filled' : 'outlined'}
                  color={criteria[field] === 'above' ? 'success' : 'default'}
                  onClick={() => toggleSmaFilter(field, 'above')} sx={{ height: 26, fontSize: '0.72rem', cursor: 'pointer' }} />
                <Chip label="Below" size="small" variant={criteria[field] === 'below' ? 'filled' : 'outlined'}
                  color={criteria[field] === 'below' ? 'error' : 'default'}
                  onClick={() => toggleSmaFilter(field, 'below')} sx={{ height: 26, fontSize: '0.72rem', cursor: 'pointer' }} />
              </Box>
            ))}
          </Box>
        );
      case 'week52':
        return (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField label="% of 52W Hi" type="number" size="small" fullWidth value={criteria.max_pct_from_52w_high ?? ''}
                onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, max_pct_from_52w_high: v }); }}
                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="% of 52W Lo" type="number" size="small" fullWidth value={criteria.max_pct_from_52w_low ?? ''}
                onChange={(e) => { const v = e.target.value === '' ? null : parseFloat(e.target.value); onFilterChange({ ...criteria, max_pct_from_52w_low: v }); }}
                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox checked={criteria.at_52w_high || false}
                  onChange={(e) => onFilterChange({ ...criteria, at_52w_high: e.target.checked || undefined })} size="small" sx={{ p: 0.25 }} />}
                label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Near 52W High</Typography>} sx={{ m: 0, mr: 1 }} />
              <FormControlLabel
                control={<Checkbox checked={criteria.at_52w_low || false}
                  onChange={(e) => onFilterChange({ ...criteria, at_52w_low: e.target.checked || undefined })} size="small" sx={{ p: 0.25 }} />}
                label={<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Near 52W Low</Typography>} sx={{ m: 0 }} />
            </FormGroup>
          </>
        );
      case 'volratio':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label=">2x" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_ratio: 2, max_vol_ratio: null })} />
              <Chip label=">5x" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_ratio: 5, max_vol_ratio: null })} />
              <Chip label="<0.5x" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_ratio: null, max_vol_ratio: 0.5 })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min Ratio" type="number" size="small" fullWidth value={criteria.min_vol_ratio ?? ''}
                onChange={(e) => handleNumberChange('min_vol_ratio', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max Ratio" type="number" size="small" fullWidth value={criteria.max_vol_ratio ?? ''}
                onChange={(e) => handleNumberChange('max_vol_ratio', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      case 'volchg':
        return (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              <Chip label=">50%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_change: 50, max_vol_change: null })} />
              <Chip label=">100%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_change: 100, max_vol_change: null })} />
              <Chip label="<-50%" size="small" variant="outlined" sx={PRESET_CHIP_SX}
                onClick={() => onFilterChange({ ...criteria, min_vol_change: null, max_vol_change: -50 })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Min %" type="number" size="small" fullWidth value={criteria.min_vol_change ?? ''}
                onChange={(e) => handleNumberChange('min_vol_change', e.target.value)}
                InputProps={{ inputProps: { step: 10 } }} sx={COMPACT_INPUT_SX} />
              <TextField label="Max %" type="number" size="small" fullWidth value={criteria.max_vol_change ?? ''}
                onChange={(e) => handleNumberChange('max_vol_change', e.target.value)}
                InputProps={{ inputProps: { step: 10 } }} sx={COMPACT_INPUT_SX} />
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  // ── Side panel — persistent across all 3 steps ────────────────────────────
  const SidePanel = (
    <Box sx={{
      width: 268, flexShrink: 0, border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2,
      display: 'flex', flexDirection: 'column', gap: 1.5, alignSelf: 'flex-start',
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.76rem', color: 'text.secondary' }}>
        Your Screen
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, maxHeight: 280, overflowY: 'auto' }}>
        {activeSummaries.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.76rem' }}>Nothing set yet</Typography>
        ) : activeSummaries.map((s, i) => (
          <Box key={i} sx={{ fontSize: '0.78rem', bgcolor: 'action.hover', borderRadius: 0.75, px: 1.25, py: 0.75 }}>{s}</Box>
        ))}
      </Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'center',
        bgcolor: bloomberg.bg.tertiary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 5, px: 2, py: 1.1,
      }}>
        {liveCountLoading ? (
          <CircularProgress size={15} />
        ) : (
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: hasUniverse ? '#22c55e' : bloomberg.text.muted,
            boxShadow: hasUniverse ? '0 0 6px #22c55e' : 'none',
          }} />
        )}
        <Typography sx={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1 }}>
          {!hasUniverse ? '—' : liveCount == null ? '—' : liveCount >= LIVE_COUNT_CAP ? `${LIVE_COUNT_CAP}+` : liveCount}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase' }}>
          {hasUniverse ? 'matches' : 'pick universe'}
        </Typography>
      </Box>

      <Button
        size="small" variant="outlined" fullWidth startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
        onClick={() => setSaveDialogOpen(true)} disabled={!hasUniverse}
        sx={{ fontSize: '0.72rem', py: 0.6, textTransform: 'none' }}
      >
        Save this screen
      </Button>

      {savedScreens.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.72rem', color: 'text.secondary', display: 'block', mb: 0.75 }}>
            Saved Screens
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {savedScreens.map(screen => (
              <Chip key={screen.id} label={screen.name} onClick={() => handleLoadSavedScreen(screen)} onDelete={() => handleDeleteSavedScreen(screen.id)}
                size="small" sx={{ cursor: 'pointer', height: 24, fontSize: '0.7rem', borderColor: bloomberg.accent.orange, color: bloomberg.accent.orange }} variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  const STEP_LABELS: [1 | 2 | 3, string][] = [[1, 'Universe'], [2, 'Add Criteria'], [3, 'Review & Screen']];
  const StepIndicator = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mb: 3 }}>
      {STEP_LABELS.map(([n, label], i) => {
        const isActive = step === n;
        const isDone = n < step;
        return (
          <React.Fragment key={n}>
            {i > 0 && <Box sx={{ width: 36, height: 2, bgcolor: isDone || isActive ? bloomberg.accent.orange : 'divider', opacity: isDone ? 0.5 : 1, mx: 1 }} />}
            <Box
              onClick={() => setStep(n)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            >
              <Box sx={{
                width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 800, fontFamily: '"IBM Plex Mono", monospace',
                border: 2,
                borderColor: isActive ? bloomberg.accent.orange : isDone ? '#22c55e' : bloomberg.border.secondary,
                color: isActive ? bloomberg.text.inverse : isDone ? '#22c55e' : bloomberg.text.muted,
                bgcolor: isActive ? bloomberg.accent.orange : 'transparent',
                boxShadow: isActive ? `0 0 12px ${bloomberg.accent.orange}55` : 'none',
                transition: 'all 0.2s ease',
              }}>
                {isDone ? '✓' : n}
              </Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isActive ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );

  const showFull = !hasSearched || forceOpen;

  // ── Collapsed summary bar — shown once a screen has run, replacing the
  // full wizard so results get the room. "Edit Screen" re-expands. ──────────
  const CollapsedBar = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      border: 1, borderColor: 'divider', borderRadius: 1, px: 2, py: 1.25,
    }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
        {activeSummaries.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>No filters set</Typography>
        ) : activeSummaries.map((s, i) => (
          <Chip key={i} label={s} size="small" variant="outlined" sx={{ height: 26, fontSize: '0.74rem' }} />
        ))}
      </Box>
      <Chip
        label={`${resultCount ?? 0} result${(resultCount ?? 0) === 1 ? '' : 's'}`}
        size="small"
        sx={{
          height: 24, fontSize: '0.72rem', fontWeight: 800, fontFamily: '"IBM Plex Mono", monospace',
          bgcolor: `${bloomberg.accent.orange}22`, color: bloomberg.accent.orange, border: `1px solid ${bloomberg.accent.orange}`,
        }}
      />
      <Button size="small" variant="outlined" onClick={() => { setForceOpen(true); setStep(3); }} sx={{ fontSize: '0.72rem' }}>
        Edit Screen
      </Button>
      {onReset && (
        <Button size="small" onClick={() => { onReset(); setForceOpen(false); setStep(1); }} sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          Reset
        </Button>
      )}
    </Box>
  );

  if (!showFull) return CollapsedBar;

  return (
    <Box>
      {(hasSearched || activeSummaries.length > 0) && onReset && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Button size="small" onClick={() => { onReset(); setForceOpen(false); setStep(1); }} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
            Start Over
          </Button>
        </Box>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem' }}>Save Current Screen</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Screen Name" value={saveScreenName} onChange={(e) => setSaveScreenName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveScreen(); }} size="small" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleSaveScreen} variant="contained" size="small" disabled={!saveScreenName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>

      {StepIndicator}

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* ── Step 1: Universe ────────────────────────────────────────── */}
          {step === 1 && (
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 0.75 }}>Choose your universe</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2, fontSize: '0.82rem', lineHeight: 1.5 }}>
                At least one region or index is required — this is what every other filter searches within.
              </Typography>

              {presets.length > 0 && (
                <Box sx={{ border: 1, borderColor: bloomberg.accent.orange, borderRadius: 1.5, p: 2, mb: 2.5, bgcolor: `${bloomberg.accent.orange}0d` }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: bloomberg.accent.orange, display: 'block', mb: 1.25 }}>
                    Or start from a preset
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {presets.map(preset => (
                      <Chip key={preset.id} label={preset.name} onClick={() => handlePresetClick(preset)} size="small" variant="outlined"
                        sx={{ cursor: 'pointer', height: 28, fontSize: '0.76rem', fontWeight: 600, borderColor: bloomberg.accent.orange, color: bloomberg.accent.orange }}
                        title={preset.description} />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {presets.length > 0 ? 'Or build manually' : 'Build your universe'}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: 'text.secondary', display: 'block', mb: 1.25 }}>
                    Regions {criteria.regions.length > 0 ? `(${criteria.regions.length})` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {broadRegions.map(br => {
                      const active = criteria.regions.includes(br.code);
                      return (
                        <Chip key={br.code} label={br.name} size="small" variant={active ? 'filled' : 'outlined'} onClick={() => handleRegionToggle(br.code)}
                          sx={{
                            height: 28, fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer',
                            ...(active ? { bgcolor: bloomberg.accent.orange, color: '#000', borderColor: bloomberg.accent.orange, '&:hover': { bgcolor: bloomberg.accent.orange, opacity: 0.85 } } : { borderColor: 'divider' }),
                          }} />
                      );
                    })}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.75 }}>Countries</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxHeight: 140, overflowY: 'auto' }}>
                    {regions.map(region => (
                      <FormControlLabel key={region.code} sx={{ m: 0, mr: 0.5, '& .MuiTypography-root': { fontSize: '0.78rem' } }}
                        control={<Checkbox checked={criteria.regions.includes(region.code)} onChange={() => handleRegionToggle(region.code)} size="small" sx={{ p: 0.25 }} />}
                        label={region.name} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: 'text.secondary', display: 'block', mb: 1.25 }}>
                    Index {(criteria.index_codes || []).length > 0 ? `(${(criteria.index_codes || []).length})` : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', display: 'block', mb: 0.75 }}>Overrides region filter</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, maxHeight: 140, overflowY: 'auto' }}>
                    {availableIndices.map(idx => (
                      <FormControlLabel key={idx.code} sx={{ m: 0, mr: 0.5, '& .MuiTypography-root': { fontSize: '0.78rem' } }}
                        control={<Checkbox checked={(criteria.index_codes || []).includes(idx.code)} onChange={() => handleIndexToggle(idx.code)} size="small" sx={{ p: 0.25 }} />}
                        label={idx.name} />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: 'text.secondary', display: 'block', mb: 1.25 }}>
                    Sectors {criteria.sectors?.length ? `(${criteria.sectors.length})` : ''}
                  </Typography>
                  <FormControl fullWidth size="small" sx={COMPACT_INPUT_SX}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Select Sectors</InputLabel>
                    <Select multiple value={criteria.sectors || []} onChange={handleSectorChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                          {selected.map((value) => <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />)}
                        </Box>
                      )}>
                      {sectors.map((sector) => (
                        <MenuItem key={sector} value={sector} sx={{ fontSize: '0.75rem' }}>
                          <Checkbox checked={(criteria.sectors || []).indexOf(sector) > -1} size="small" />{sector}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: 'text.secondary', display: 'block', mb: 1.25 }}>
                    Industries {criteria.industries?.length ? `(${criteria.industries.length})` : ''}
                  </Typography>
                  <FormControl fullWidth size="small" sx={COMPACT_INPUT_SX}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Select Industries</InputLabel>
                    <Select multiple value={criteria.industries || []} onChange={handleIndustryChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                          {selected.map((value) => <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />)}
                        </Box>
                      )}>
                      {industries.map((industry) => (
                        <MenuItem key={industry} value={industry} sx={{ fontSize: '0.75rem' }}>
                          <Checkbox checked={(criteria.industries || []).indexOf(industry) > -1} size="small" />{industry}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <Button variant="contained" endIcon={<ArrowForwardIcon />} disabled={!hasUniverse} onClick={() => setStep(2)} sx={{ fontSize: '0.8rem', py: 1, px: 2.5, fontWeight: 700 }}>
                  Next: Add Criteria
                </Button>
                {!hasUniverse && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Select at least one region or index to continue
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* ── Step 2: Add Criteria ────────────────────────────────────── */}
          {step === 2 && (
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 0.75 }}>Add criteria</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, fontSize: '0.82rem', lineHeight: 1.5 }}>
                Search or browse by category — only what you add shows up below.
              </Typography>
              <TextField
                size="small" fullWidth placeholder='Search filters… e.g. "debt", "RSI", "dividend"'
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                sx={{ mb: 2, ...COMPACT_INPUT_SX }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {CATEGORIES.map(cat => {
                  const items = FILTER_REGISTRY.filter(f => f.category === cat && (!searchLower || f.label.toLowerCase().includes(searchLower)));
                  if (items.length === 0) return null;
                  return (
                    <Box key={cat}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.75 }}>
                        {cat}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {items.map(f => {
                          const added = manuallyAdded.has(f.key) || f.isActive(criteria);
                          return (
                            <Chip
                              key={f.key}
                              label={f.label}
                              size="small"
                              icon={added ? <CheckIcon sx={{ fontSize: '14px !important' }} /> : undefined}
                              onClick={() => (added ? undefined : addFilter(f.key))}
                              sx={{
                                height: 32, fontSize: '0.8rem', fontWeight: 600, cursor: added ? 'default' : 'pointer',
                                ...(added
                                  ? { color: '#22c55e', borderColor: '#22c55e', bgcolor: 'rgba(34,197,94,0.08)' }
                                  : { borderColor: 'divider' }),
                              }}
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {visibleGroups.length > 0 && (
                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.74rem', color: 'text.secondary', display: 'block', mb: 1.25 }}>
                    Active Criteria ({visibleGroups.length})
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                    {visibleGroups.map(f => (
                      <Box key={f.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{f.label}</Typography>
                          <CloseIcon sx={{ fontSize: 15, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'error.main' } }} onClick={() => removeFilter(f)} />
                        </Box>
                        {renderFilterInputs(f.key)}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setStep(1)} sx={{ fontSize: '0.8rem', py: 1, px: 2.5 }}>Back</Button>
                <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => setStep(3)} sx={{ fontSize: '0.8rem', py: 1, px: 2.5, fontWeight: 700 }}>Next: Review</Button>
              </Box>
            </Box>
          )}

          {/* ── Step 3: Review & Screen ─────────────────────────────────── */}
          {step === 3 && (
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 0.75 }}>Review &amp; screen</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2, fontSize: '0.82rem', lineHeight: 1.5 }}>
                {activeSummaries.length} active criteria. Adjust the results limit, then run the screen.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                {activeSummaries.map((s, i) => (
                  <Box key={i} sx={{ fontSize: '0.78rem', bgcolor: 'action.hover', borderRadius: 0.5, px: 1.5, py: 0.85 }}>{s}</Box>
                ))}
              </Box>
              <Box sx={{ maxWidth: 240, mb: 2.5 }}>
                <TextField
                  label="Max Results" type="number" size="small" fullWidth value={criteria.max_results || 100}
                  onChange={(e) => { const value = parseInt(e.target.value) || 100; onFilterChange({ ...criteria, max_results: value }); }}
                  InputProps={{ inputProps: { min: 10, max: 1000, step: 10 } }} sx={COMPACT_INPUT_SX}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setStep(2)} sx={{ fontSize: '0.8rem', py: 1, px: 2.5 }}>Edit criteria</Button>
                <Button variant="contained" disabled={loading || !hasUniverse} onClick={() => { setForceOpen(false); onSearch(); }} sx={{ fontSize: '0.85rem', fontWeight: 700, py: 1, px: 3 }}>
                  {loading ? <CircularProgress size={16} /> : 'Run Screen'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {SidePanel}
      </Box>
    </Box>
  );
};

export default ScreenerWizard;
